import {useEffect, useRef} from 'react';
import {UseQueryResultWithUpdateCache, useQueryClient} from './react-query';
import {Config as UseRequestConfig} from './useRequest';

/**
 * This hook returns the request with stale fallback data (if exists) in case of a client side error.
 */
export const useQueryWithStaleFallbackData = <Data extends object>({
  request,
  cacheKey,
  queryConfig,
}: {
  request: UseQueryResultWithUpdateCache<Data, Error>;
  cacheKey: string;
  queryConfig?: UseRequestConfig<Data>;
}) => {
  if (!process.browser || !queryConfig?.keepPreviousDataOnClientError) {
    return request;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryClient = useQueryClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const staleFallbackData = useRef<Data | undefined>(undefined);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (request.error && staleFallbackData.current) {
      queryClient.setQueryData(cacheKey, staleFallbackData.current);
      staleFallbackData.current = undefined;
    }
  }, [cacheKey, queryClient, request.error]);

  const canSetStaleFallbackData =
    queryConfig?.keepPreviousDataOnClientError &&
    queryConfig.keepPreviousData &&
    request.data;

  if (canSetStaleFallbackData) {
    staleFallbackData.current = request.data;
  } else if (queryConfig?.keepPreviousData === false) {
    staleFallbackData.current = undefined;
  }

  // if an error occurred and we have stale fallback data, return it as the data with no error
  // and update the request's cache as a side effect in useEffect above
  if (request.error && staleFallbackData.current) {
    return {...request, data: staleFallbackData.current, error: null};
  }

  return request;
};
